"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@clerk/nextjs";
import { apiFetch } from "@/lib/api";

export interface SavedSummary {
  id: number;
  question: string;
  mode: string;
  answer: string;
  sources: unknown[];
  created_at: string;
}

interface Props {
  onLoad: (summary: SavedSummary) => void;
  /** Called after a delete so the parent can hide the header menu item. */
  onChanged: (hasAny: boolean) => void;
}

/** Saved summaries rendered as a view tab: list, load or delete. */
export default function SavedList({ onLoad, onChanged }: Props) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<SavedSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/saved-answers", { token: (await getToken()) ?? undefined });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as SavedSummary[];
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) {
          setError(`Kunde inte hämta sparade sammanfattningar (${e instanceof Error ? e.message : e}).`);
          setItems([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  async function loadOne(id: number) {
    setBusyId(id);
    try {
      const res = await apiFetch(`/saved-answers/${id}`, { token: (await getToken()) ?? undefined });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onLoad((await res.json()) as SavedSummary);
    } catch (e) {
      setError(`Kunde inte ladda sammanfattningen (${e instanceof Error ? e.message : e}).`);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id: number) {
    if (!window.confirm("Ta bort denna sparade sammanfattning?")) return;
    try {
      const res = await apiFetch(`/saved-answers/${id}`, { method: "DELETE", token: (await getToken()) ?? undefined });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const remaining = (items ?? []).filter((s) => s.id !== id);
      setItems(remaining);
      onChanged(remaining.length > 0);
    } catch (e) {
      setError(`Kunde inte ta bort (${e instanceof Error ? e.message : e}).`);
    }
  }

  return (
    <div className="saved-panel">
      {error && <div className="callout callout--boxed callout--warn error-box">{error}</div>}
      {items === null && <p className="empty">Hämtar lista…</p>}
      {items !== null && items.length === 0 && !error && (
        <p className="empty">Inga sparade sammanfattningar ännu.</p>
      )}
      <div className="saved-list card-grid">
        {items?.map((s) => (
          <div className="card card--sm card--flat source-item cite-card saved-card" key={s.id}>
            <button className="btn-bare saved-load" onClick={() => loadOne(s.id)} disabled={busyId === s.id}>
              <span className="saved-q">{s.question}</span>
            </button>
            <div className="meta saved-meta">
              <span className="saved-date">
                {new Date(s.created_at).toLocaleDateString("sv-SE", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
              <button className="btn-bare btn-danger" aria-label="Ta bort" title="Ta bort" onClick={() => remove(s.id)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Ta bort
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
